import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { notifications } from '@mantine/notifications'
import type { AuthStore, User, LoginResponse, RefreshResponse } from '@/types'
import apiClient from '@/api/client'

const TOKEN_KEY = 'rustswarm_access_token'
const REFRESH_TOKEN_KEY = 'rustswarm_refresh_token'

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,

      initialize: async () => {
        const accessToken = localStorage.getItem(TOKEN_KEY)
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)

        if (!accessToken || !refreshToken) {
          set({ isLoading: false, isAuthenticated: false })
          return
        }

        set({ accessToken, refreshToken, isLoading: true })

        try {
          await get().fetchUser()
          set({ isAuthenticated: true })
        } catch (error) {
          // Token might be expired, try to refresh
          try {
            await get().refreshAccessToken()
            await get().fetchUser()
            set({ isAuthenticated: true })
          } catch (refreshError) {
            // Refresh failed, clear auth state
            get().logout()
          }
        } finally {
          set({ isLoading: false })
        }
      },

      login: async (username: string, password: string) => {
        set({ isLoading: true })
        try {
          const response = await apiClient.post<LoginResponse>('/v1/auth/login', {
            username,
            password,
          })

          const { access_token, refresh_token } = response.data

          // Store tokens
          localStorage.setItem(TOKEN_KEY, access_token)
          localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token)

          set({
            accessToken: access_token,
            refreshToken: refresh_token,
          })

          // Fetch user info
          await get().fetchUser()

          set({ isAuthenticated: true, isLoading: false })

          notifications.show({
            title: 'Success',
            message: 'Logged in successfully',
            color: 'green',
          })
        } catch (error: any) {
          set({ isLoading: false })
          const message = error.response?.data?.error || 'Login failed'
          notifications.show({
            title: 'Login Failed',
            message,
            color: 'red',
          })
          throw error
        }
      },

      logout: async () => {
        const { accessToken } = get()

        // Call logout endpoint
        if (accessToken) {
          try {
            await apiClient.post('/v1/auth/logout')
          } catch (error) {
            // Ignore logout errors
            console.error('Logout error:', error)
          }
        }

        // Clear tokens
        localStorage.removeItem(TOKEN_KEY)
        localStorage.removeItem(REFRESH_TOKEN_KEY)

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        })

        notifications.show({
          title: 'Logged Out',
          message: 'You have been logged out successfully',
          color: 'blue',
        })
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get()

        if (!refreshToken) {
          throw new Error('No refresh token available')
        }

        try {
          const response = await apiClient.post<RefreshResponse>('/v1/auth/refresh', {
            refresh_token: refreshToken,
          })

          const { access_token, refresh_token: new_refresh_token } = response.data

          // Update tokens
          localStorage.setItem(TOKEN_KEY, access_token)
          localStorage.setItem(REFRESH_TOKEN_KEY, new_refresh_token)

          set({
            accessToken: access_token,
            refreshToken: new_refresh_token,
          })
        } catch (error) {
          // Refresh failed, logout user
          await get().logout()
          throw error
        }
      },

      fetchUser: async () => {
        try {
          const response = await apiClient.get<User>('/v1/auth/whoami')
          set({ user: response.data })
        } catch (error) {
          throw error
        }
      },
    }),
    {
      name: 'rustswarm-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
    }
  )
)
